import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";
import EmptyState from "../../../components/shared/EmptyState";
import LoadingState from "../../../components/shared/LoadingState";
import ErrorState from "../../../components/shared/ErrorState";
import { toast } from "../../../components/shared/toast";

const STATUS_COLORS = {
  DRAFT:    "bg-slate-100 text-slate-700",
  SENT:     "bg-blue-100 text-blue-700",
  VIEWED:   "bg-amber-100 text-amber-700",
  SIGNED:   "bg-emerald-100 text-emerald-700",
  ACCEPTED: "bg-emerald-100 text-emerald-700",
  DECLINED: "bg-red-100 text-red-700",
  REVOKED:  "bg-slate-200 text-slate-700",
  EXPIRED:  "bg-slate-100 text-slate-500",
};

const fmt = (iso) => iso ? new Date(iso).toLocaleString() : "—";

export default function OfferList() {
  const nav = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorStatus, setErrorStatus] = useState(null);

  const load = () => {
    setLoading(true);
    setErrorStatus(null);
    axiosInstance.get(`${baseUrl}/job-posting/offers`)
      .then((r) => setOffers(Array.isArray(r.data) ? r.data : []))
      .catch((e) => {
        setOffers([]);
        setErrorStatus(e?.status || e?.response?.status || 500);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "Offers - Ample Hire";
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async (id) => {
    try {
      const r = await axiosInstance.post(`${baseUrl}/job-posting/offers/${id}/send-for-signing`);
      setOffers((prev) => prev.map((o) => o.id === id
        ? { ...o, status: "SENT", signatureToken: r.data.publicToken }
        : o));
      toast.success(`Offer #${id} sent for signing`);
    } catch (e) {
      toast.error("Send failed", { description: e?.data?.error || e?.message });
    }
  };

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Offers</h1>
          <p className="text-sm text-vf-muted mt-1">All offers created — drafts, sent, accepted, declined.</p>
        </div>
        <button onClick={() => nav("/admin/offers/new")} className="px-3 py-1.5 rounded bg-vf-primary text-white text-xs">
          + New offer
        </button>
      </div>

      {errorStatus && !loading ? (
        <ErrorState status={errorStatus} retry={load} />
      ) : !loading && offers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl">
          <EmptyState
            title="No offers yet"
            description="Draft an offer to a shortlisted candidate. Once sent, you'll see status, response, and audit history here."
            primaryAction={{ label: "+ New offer", to: "/admin/offers/new" }}
          />
        </div>
      ) : (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-vf-muted">
            <tr>
              <th className="text-left px-4 py-2 font-medium">ID</th>
              <th className="text-left px-4 py-2 font-medium">Candidate</th>
              <th className="text-left px-4 py-2 font-medium">Job</th>
              <th className="text-left px-4 py-2 font-medium">Salary</th>
              <th className="text-left px-4 py-2 font-medium">Status</th>
              <th className="text-left px-4 py-2 font-medium">Sent</th>
              <th className="text-left px-4 py-2 font-medium">Response</th>
              <th className="text-right px-4 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={8} className="px-4 py-6">
                <LoadingState variant="skeleton" shape="row" count={4} />
              </td></tr>
            )}
            {offers.map((o) => (
              <tr key={o.id}>
                <td className="px-4 py-2 text-vf-muted text-xs">#{o.id}</td>
                <td className="px-4 py-2 text-vf-base">#{o.candidateId}</td>
                <td className="px-4 py-2 text-vf-base">#{o.jobId}</td>
                <td className="px-4 py-2 text-vf-base">{o.salaryAmount ? `${o.salaryCurrency} ${o.salaryAmount}` : "—"}</td>
                <td className="px-4 py-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[o.status] || "bg-slate-100"}`}>
                    {o.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-vf-muted text-xs">{fmt(o.sentAt)}</td>
                <td className="px-4 py-2 text-vf-muted text-xs">{o.candidateResponse || "—"}</td>
                <td className="px-4 py-2 text-right space-x-1">
                  <button onClick={() => nav(`/admin/offers/${o.id}`)} className="px-2 py-1 rounded border border-slate-300 text-xs">
                    Detail
                  </button>
                  {o.status === "DRAFT" && (
                    <button onClick={() => send(o.id)} className="px-2 py-1 rounded bg-vf-primary text-white text-xs">Send</button>
                  )}
                  {o.signatureToken && (
                    <a href={`/offer/${o.signatureToken}`} target="_blank" rel="noreferrer" className="px-2 py-1 rounded border border-slate-300 text-xs inline-block">
                      View link
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}
